import { useState, useEffect } from "react";
import { useParams } from "react-router";
import {
  Loader2,
  AlertTriangle,
  Building2,
  Briefcase,
  FileText,
  DollarSign,
  CheckCircle2,
  Clock,
  Play,
  Calendar,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Activity,
} from "lucide-react";

// ============================================================================
// Types
// ============================================================================

interface PortalJob {
  id: string;
  job_number: string;
  status: string;
  title: string;
  description: string;
  scheduled_start: string | null;
  scheduled_end: string | null;
  actual_start: string | null;
  actual_end: string | null;
  completed_at: string | null;
  created_at: string;
}

interface PortalEstimate {
  id: string;
  estimate_number: string;
  status: string;
  title: string;
  total: number;
  estimated_days: number | null;
  access_token: string;
  created_at: string;
}

interface PortalInvoice {
  id: string;
  invoice_number: string;
  invoice_type: string;
  status: string;
  title: string;
  total: number;
  checkout_url: string | null;
  paid_at: string | null;
  created_at: string;
}

interface PortalEvent {
  id: string;
  event_type: string;
  payload: Record<string, unknown>;
  created_at: string;
}

interface PortalData {
  contact: { first_name: string; last_name: string; name: string };
  org: { name: string };
  jobs: PortalJob[];
  estimates: PortalEstimate[];
  invoices: PortalInvoice[];
  timeline: PortalEvent[];
  summary: {
    total_jobs: number;
    active_jobs: number;
    total_estimates: number;
    pending_estimates: number;
    total_invoiced: number;
    total_paid: number;
    outstanding: number;
  };
}

// ============================================================================
// Helpers
// ============================================================================

function fmt(n: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(n);
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || "";

// ============================================================================
// Job status config
// ============================================================================

const JOB_STATUS: Record<
  string,
  {
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    color: string;
    bg: string;
  }
> = {
  created: { label: "Created", icon: Clock, color: "#6b7280", bg: "#f3f4f6" },
  scheduled: {
    label: "Scheduled",
    icon: Calendar,
    color: "#2563eb",
    bg: "#dbeafe",
  },
  in_progress: {
    label: "In Progress",
    icon: Play,
    color: "#d97706",
    bg: "#fef3c7",
  },
  completed: {
    label: "Completed",
    icon: CheckCircle2,
    color: "#16a34a",
    bg: "#dcfce7",
  },
  closed: {
    label: "Closed",
    icon: CheckCircle2,
    color: "#6b7280",
    bg: "#e5e7eb",
  },
};

// ============================================================================
// Event labels for timeline
// ============================================================================

const EVENT_LABELS: Record<string, string> = {
  estimate_sent: "Estimate sent",
  estimate_accepted: "Estimate accepted",
  estimate_declined: "Estimate declined",
  invoice_sent: "Invoice sent",
  invoice_paid: "Payment received",
  job_created: "Job created",
  job_scheduled: "Job scheduled",
  job_started: "Work started",
  job_completed: "Job completed",
  job_closed: "Job closed",
  status_change: "Status updated",
};

// ============================================================================
// Expandable section component
// ============================================================================

function Section({
  title,
  icon: Icon,
  count,
  defaultOpen = true,
  children,
}: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  count?: number;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-xl border overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-3.5 text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-semibold">{title}</span>
          {count !== undefined && count > 0 && (
            <span className="text-xs text-muted-foreground bg-gray-100 px-1.5 py-0.5 rounded-full">
              {count}
            </span>
          )}
        </div>
        {open ? (
          <ChevronUp className="w-4 h-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="w-4 h-4 text-muted-foreground" />
        )}
      </button>
      {open && <div className="border-t">{children}</div>}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function CustomerPortalPage() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<PortalData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setError("Invalid link");
      setLoading(false);
      return;
    }

    fetch(`${SUPABASE_URL}/functions/v1/customer-portal?token=${token}`)
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || `Error ${res.status}`);
        }
        return res.json();
      })
      .then((d: PortalData) => setData(d))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [token]);

  // --- LOADING ---
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-3" />
          <p className="text-sm text-gray-500">Loading your dashboard…</p>
        </div>
      </div>
    );
  }

  // --- ERROR ---
  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="text-center max-w-sm">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
          <h1 className="text-xl font-bold mb-2">Portal Not Found</h1>
          <p className="text-sm text-gray-500">
            {error || "This link may be invalid."}
          </p>
        </div>
      </div>
    );
  }

  const { contact, org, jobs, estimates, invoices, timeline, summary } = data;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div
              className="flex items-center justify-center w-10 h-10 rounded-xl shrink-0"
              style={{ backgroundColor: "#dbeafe" }}
            >
              <Building2 className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="font-semibold">{org.name}</p>
              <p className="text-xs text-gray-500">
                Welcome, {contact.first_name || contact.name}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        {/* Summary cards */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white rounded-xl border p-4 text-center">
            <p className="text-2xl font-bold text-blue-600">
              {summary.active_jobs}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">Active Jobs</p>
          </div>
          <div className="bg-white rounded-xl border p-4 text-center">
            <p className="text-2xl font-bold text-green-600">
              {fmt(summary.total_paid)}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">Paid</p>
          </div>
          <div className="bg-white rounded-xl border p-4 text-center">
            <p
              className="text-2xl font-bold"
              style={{
                color: summary.outstanding > 0 ? "#d97706" : "#16a34a",
              }}
            >
              {fmt(summary.outstanding)}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">Outstanding</p>
          </div>
        </div>

        {/* Jobs */}
        {jobs.length > 0 && (
          <Section title="Your Projects" icon={Briefcase} count={jobs.length}>
            <div className="divide-y">
              {jobs.map((job) => {
                const sc = JOB_STATUS[job.status] || JOB_STATUS.created;
                const Icon = sc.icon;
                return (
                  <div key={job.id} className="px-5 py-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-xs font-mono text-muted-foreground">
                            {job.job_number}
                          </span>
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                            style={{ color: sc.color, backgroundColor: sc.bg }}
                          >
                            <Icon className="w-3 h-3" />
                            {sc.label}
                          </span>
                        </div>
                        <p className="text-sm font-medium">
                          {job.title || "Untitled"}
                        </p>
                        {job.description && (
                          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                            {job.description}
                          </p>
                        )}
                      </div>
                      <div className="text-right text-xs text-muted-foreground shrink-0 ml-3">
                        {job.scheduled_start && (
                          <p>Starts {shortDate(job.scheduled_start)}</p>
                        )}
                        {job.actual_start && !job.completed_at && (
                          <p>Started {shortDate(job.actual_start)}</p>
                        )}
                        {job.completed_at && (
                          <p className="text-green-600">
                            Done {shortDate(job.completed_at)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Section>
        )}

        {/* Estimates */}
        {estimates.length > 0 && (
          <Section
            title="Estimates"
            icon={FileText}
            count={estimates.length}
            defaultOpen={estimates.some((e) => e.status === "sent")}
          >
            <div className="divide-y">
              {estimates.map((est) => {
                const isPending = est.status === "sent";
                return (
                  <div key={est.id} className="px-5 py-3">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-xs font-mono text-muted-foreground">
                            {est.estimate_number}
                          </span>
                          {isPending && (
                            <span className="text-xs font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">
                              Needs Response
                            </span>
                          )}
                          {est.status === "accepted" && (
                            <span className="text-xs font-medium text-green-600 bg-green-50 px-1.5 py-0.5 rounded">
                              Accepted
                            </span>
                          )}
                          {est.status === "declined" && (
                            <span className="text-xs font-medium text-red-500 bg-red-50 px-1.5 py-0.5 rounded">
                              Declined
                            </span>
                          )}
                        </div>
                        <p className="text-sm font-medium">
                          {est.title || "Untitled"}
                        </p>
                      </div>
                      <div className="text-right shrink-0 ml-3">
                        <p className="text-sm font-bold tabular-nums">
                          {fmt(Number(est.total))}
                        </p>
                        {isPending && est.access_token && (
                          <a
                            href={`/estimate/${est.access_token}`}
                            className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline mt-0.5"
                          >
                            View & Respond
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Section>
        )}

        {/* Invoices */}
        {invoices.length > 0 && (
          <Section
            title="Invoices & Payments"
            icon={DollarSign}
            count={invoices.length}
            defaultOpen={invoices.some(
              (i) => i.status === "sent" && i.checkout_url
            )}
          >
            <div className="divide-y">
              {invoices.map((inv) => {
                const canPay = inv.status === "sent" && inv.checkout_url;
                const isPaid = inv.status === "paid";
                return (
                  <div key={inv.id} className="px-5 py-3">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-xs font-mono text-muted-foreground">
                            {inv.invoice_number}
                          </span>
                          {isPaid && (
                            <span className="text-xs font-medium text-green-600 bg-green-50 px-1.5 py-0.5 rounded">
                              Paid
                            </span>
                          )}
                          {canPay && (
                            <span className="text-xs font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">
                              Payment Due
                            </span>
                          )}
                          {inv.status === "failed" && (
                            <span className="text-xs font-medium text-red-500 bg-red-50 px-1.5 py-0.5 rounded">
                              Failed
                            </span>
                          )}
                        </div>
                        <p className="text-sm font-medium">
                          {inv.title || "Untitled"}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {inv.invoice_type !== "standard"
                            ? inv.invoice_type.charAt(0).toUpperCase() +
                              inv.invoice_type.slice(1)
                            : "Invoice"}{" "}
                          · {shortDate(inv.created_at)}
                        </p>
                      </div>
                      <div className="text-right shrink-0 ml-3">
                        <p className="text-sm font-bold tabular-nums">
                          {fmt(Number(inv.total))}
                        </p>
                        {canPay && (
                          <a
                            href={inv.checkout_url!}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 mt-1 px-3 py-1 rounded-lg text-xs font-semibold text-white bg-green-600 hover:bg-green-700 transition-colors"
                          >
                            Pay Now
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                        {isPaid && inv.paid_at && (
                          <p className="text-xs text-green-600 mt-0.5">
                            {shortDate(inv.paid_at)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Section>
        )}

        {/* Timeline */}
        {timeline.length > 0 && (
          <Section
            title="Activity"
            icon={Activity}
            count={timeline.length}
            defaultOpen={false}
          >
            <div className="divide-y">
              {timeline.map((ev) => (
                <div key={ev.id} className="px-5 py-2.5">
                  <div className="flex items-center justify-between">
                    <p className="text-sm">
                      {EVENT_LABELS[ev.event_type] || ev.event_type}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {shortDate(ev.created_at)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Empty state */}
        {jobs.length === 0 &&
          estimates.length === 0 &&
          invoices.length === 0 && (
            <div className="bg-white rounded-xl border p-8 text-center">
              <Building2 className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-40" />
              <h2 className="text-lg font-semibold mb-1">
                Welcome to your project dashboard
              </h2>
              <p className="text-sm text-muted-foreground">
                {org.name} will share project updates, estimates, and invoices
                here.
              </p>
            </div>
          )}

        {/* Footer */}
        <div className="text-center text-xs text-muted-foreground pt-4 pb-8">
          Powered by BldrOS · {org.name}
        </div>
      </div>
    </div>
  );
}

CustomerPortalPage.path = "/portal/:token";
